/**************************************************************************************************/
/*   File: /src/app/app.module.ts                                                                 */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Tue May 14 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { localStorageSync } from 'ngrx-store-localstorage';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { MomentModule } from 'ngx-moment';

import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Injector, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { ActionReducer, ActionReducerMap, MetaReducer, StoreModule } from '@ngrx/store';
import { EgovModule } from './egov/egov.module';
/**
 * Redux import
 */
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { environment } from '../environments/environment.prod';
/**
 * The routing
 */
import { AppRoutingModule } from './app-routing.module';
/**
 * The components
 */
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import * as fromEffects from './stores/effects';
/**
 * Import redux internal
 */
import * as fromReducers from './stores/reducers';

// AoT requires an exported function for factories
/**
 * The custom translate loader
 * @param http The http client
 */
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/language.', '.json');
}

/**
 * The local storage sync reducer
 */
export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync(
    {
      keys: [
        'ui',
        'authorization',
        'download',
        'router',
        'dossiers'
      ],
      rehydrate: true
    }
  )(reducer);
}
/**
 * The metareducers
 */
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];


/**
 * The application module
 */
@NgModule({
  declarations: [
    AppComponent
  ],
  entryComponents: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    AppRoutingModule,
    MomentModule,
    SharedModule,
    EgovModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      isolate: false
    }),
    LoggerModule.forRoot({ level: environment.logLevel }),
    // Redux
    StoreModule.forRoot(fromReducers.fromRoot.rootReducer, { metaReducers }),
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([
      fromEffects.fromRouter.RouterEffects,
      fromEffects.fromAuthorization.AuthorizationEffects,
      fromEffects.fromDownload.DownloadEffects,
      fromEffects.fromDossiers.DossiersEffects,
      fromEffects.fromUi.UiEffects,
      fromEffects.fromCreation.CreationEffects,
      fromEffects.fromDetails.DetailsEffects,
      fromEffects.fromEditing.EditingEffects
    ]),
    StoreDevtoolsModule.instrument({ maxAge: 25 }),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router'
    })
  ],
  providers: [
  ],
  bootstrap: [
    AppComponent
  ]
})
export class AppModule {

  /**
   * The app module
   * @param injector The injector
   */
  constructor(private injector: Injector) {

  }
  /**
   * The bootstrapper for element
   */
  ngDoBootstrap() {
    const el = createCustomElement(AppComponent, { injector: this.injector });
    customElements.define('newplan-frontend', el);
  }
}
